import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  CheckCircleOutlineRounded,
  CheckCircleRounded,
  DeleteOutlineRounded,
  FavoriteRounded,
  FavoriteBorderRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../components/ConfirmProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import {
  prayerApi,
  type PrayerRequestDto,
  type PrayerScopeType,
} from '../../../lib/api/prayer.api';
import { useEnrolledSections } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { RichSpiritualText } from '../scripture/RichSpiritualText';
import { errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';

export function PrayerBoardPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const qc = useQueryClient();
  const sectionsQ = useEnrolledSections();
  const [dialogOpen, setDialogOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ['portal-v2-prayer', currentTenantId, personId],
    enabled: !!currentTenantId,
    queryFn: () =>
      prayerApi.list({
        tenantId: currentTenantId!,
        ...(personId ? { personId } : {}),
      }),
  });

  const togglePray = useMutation({
    mutationFn: (requestId: string) =>
      prayerApi.togglePray({
        tenantId: currentTenantId!,
        requestId,
        ...(personId ? { personId } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-v2-prayer'] }),
  });

  const markAnswered = useMutation({
    mutationFn: ({ requestId, answered }: { requestId: string; answered: boolean }) =>
      prayerApi.markAnswered({
        tenantId: currentTenantId!,
        requestId,
        answered,
        ...(personId ? { personId } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-v2-prayer'] }),
  });

  const remove = useMutation({
    mutationFn: (requestId: string) =>
      prayerApi.delete({
        tenantId: currentTenantId!,
        requestId,
        ...(personId ? { personId } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-v2-prayer'] }),
  });

  if (listQ.isLoading || sectionsQ.isLoading) return <PortalLoading />;

  const items = listQ.data?.items ?? [];
  const currentPersonId = listQ.data?.currentPersonId ?? personId ?? '';

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h4" fontWeight={700} color="#e6edf3">
            {t('portal.prayer.title')}
          </Typography>
          <Typography variant="body2" color="#8b949e">
            {t('portal.prayer.subtitle')}
          </Typography>
        </Box>
        <Button
          startIcon={<AddRounded />}
          variant="contained"
          onClick={() => setDialogOpen(true)}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, flexShrink: 0 }}
        >
          {t('portal.prayer.newRequest')}
        </Button>
      </Stack>

      {items.length === 0 && (
        <PortalEmpty
          icon={<FavoriteBorderRounded fontSize="inherit" />}
          title={t('portal.prayer.emptyTitle')}
          body={t('portal.prayer.emptyBody')}
        />
      )}

      {items.map((req) => (
        <PrayerCard
          key={req.id}
          req={req}
          currentPersonId={currentPersonId}
          onPray={() => togglePray.mutate(req.id)}
          onAnswered={() =>
            markAnswered.mutate({ requestId: req.id, answered: req.status === 'open' })
          }
          onDelete={async () => {
            if (await confirm({ message: t('portal.prayer.confirmDelete'), confirmColor: 'error' }))
              remove.mutate(req.id);
          }}
        />
      ))}

      {dialogOpen && (
        <NewPrayerDialog
          onClose={() => setDialogOpen(false)}
          onCreated={() => {
            setDialogOpen(false);
            qc.invalidateQueries({ queryKey: ['portal-v2-prayer'] });
          }}
          enrolledSections={(sectionsQ.data?.enrolledSections ?? []).map((s) => ({
            id: s.id,
            label: `${s.courseCode} — ${s.courseName}`,
          }))}
        />
      )}
    </Stack>
  );
}

function PrayerCard({
  req,
  currentPersonId,
  onPray,
  onAnswered,
  onDelete,
}: {
  req: PrayerRequestDto;
  currentPersonId: string;
  onPray: () => void;
  onAnswered: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const isAuthor = req.authorPersonId === currentPersonId;
  const isPraying = req.prayedBy.includes(currentPersonId);
  const scopeLabel =
    req.scope.type === 'seminary'
      ? t('portal.prayer.scopeSeminary')
      : req.scope.type === 'program'
        ? t('portal.prayer.scopeProgram')
        : t('portal.prayer.scopeCourse');
  return (
    <Card
      sx={{
        bgcolor: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 2,
        p: 2.5,
        ...(req.status === 'answered'
          ? { borderColor: '#10b981', bgcolor: 'rgba(16,185,129,0.04)' }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.5}>
        <Avatar sx={{ bgcolor: '#6366f1', width: 36, height: 36, fontSize: 14 }}>
          {req.authorName
            .split(' ')
            .map((n) => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
            {req.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="#8b949e">
              {req.authorName}
            </Typography>
            <Chip
              size="small"
              label={scopeLabel}
              variant="outlined"
              sx={{ height: 18, fontSize: 10, borderColor: '#30363d' }}
            />
            <Typography variant="caption" color="#6e7681">
              {formatDate(req.createdAt, {
                day: 'numeric',
                month: 'short',
              })}
            </Typography>
            {req.status === 'answered' && (
              <Chip
                size="small"
                icon={<CheckCircleRounded sx={{ fontSize: 12 }} />}
                label={t('portal.prayer.answered')}
                sx={{ height: 20, fontSize: 11, bgcolor: '#10b981', color: '#fff' }}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
        <RichSpiritualText>{req.body}</RichSpiritualText>
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          startIcon={isPraying ? <FavoriteRounded /> : <FavoriteBorderRounded />}
          onClick={onPray}
          sx={{
            color: isPraying ? '#ec4899' : '#8b949e',
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(236,72,153,0.06)' },
          }}
        >
          {t('portal.prayer.prayingCount', { n: req.prayedBy.length })}
        </Button>
        {isAuthor && (
          <>
            <Button
              size="small"
              startIcon={<CheckCircleOutlineRounded />}
              onClick={onAnswered}
              sx={{ color: '#8b949e', textTransform: 'none' }}
            >
              {req.status === 'answered'
                ? t('portal.prayer.markPending')
                : t('portal.prayer.markAnswered')}
            </Button>
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteOutlineRounded fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
    </Card>
  );
}

function NewPrayerDialog({
  onClose,
  onCreated,
  enrolledSections,
}: {
  onClose: () => void;
  onCreated: () => void;
  enrolledSections: { id: string; label: string }[];
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scopeType, setScopeType] = useState<PrayerScopeType>('seminary');
  const [scopeId, setScopeId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave =
    title.trim().length > 0 && body.trim().length > 0 && (scopeType === 'seminary' || !!scopeId);

  const save = async () => {
    if (!currentTenantId) return;
    setSaving(true);
    setError(null);
    try {
      await prayerApi.create({
        tenantId: currentTenantId,
        ...(personId ? { personId } : {}),
        scope: scopeType === 'seminary' ? { type: 'seminary' } : { type: scopeType, id: scopeId },
        title: title.trim(),
        body: body.trim(),
      });
      onCreated();
    } catch (e) {
      setError(errorText(e, t('portal.prayer.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('portal.prayer.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('portal.prayer.fieldTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={t('portal.prayer.fieldBody')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
          />
          <TextField
            select
            label={t('portal.prayer.fieldScope')}
            value={scopeType}
            onChange={(e) => {
              setScopeType(e.target.value as PrayerScopeType);
              setScopeId('');
            }}
            fullWidth
            size="small"
          >
            <MenuItem value="seminary">{t('portal.prayer.scopeAllSeminary')}</MenuItem>
            <MenuItem value="section" disabled={enrolledSections.length === 0}>
              {t('portal.prayer.scopeSpecificCourse')}
            </MenuItem>
          </TextField>
          {scopeType === 'section' && (
            <TextField
              select
              label={t('portal.prayer.fieldCourse')}
              value={scopeId}
              onChange={(e) => setScopeId(e.target.value)}
              fullWidth
              size="small"
            >
              {enrolledSections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('portal.prayer.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? t('portal.prayer.saving') : t('portal.prayer.publish')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
